import { useCallback, useMemo } from 'react'
import useAllUserCartItemsMetadata, { CartItemData } from './useAllUserCartItemsMetadata'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { UserCartHotelItemData } from './useUserCartHotelItemData'
import { UserCartBedbankHotelItemData } from './useUserCartBedbankHotelItemData'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { userCartItemsDelete, userCartItemsSaveForLater } from 'actions/UserCartActions'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import { selectCartOffers } from 'selectors/userCart'
import { isLEHotel } from 'lib/offer/offerTypes'
import { BannerTypeVariant, getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'

interface MultiRoomGroupData {
  hotelName: string
  publicTotalPrice: number | undefined
  fetching: boolean
  error: boolean
  areAllItemsSavedForLater: boolean
  areAllItemsDeleted: boolean
  isAnyItemDeleted: boolean
  cartItemsData: Array<CartItemData>
  groupDisabled: boolean
  bannerTypeVariant: BannerTypeVariant
  handleSaveAllForLater: (e: React.MouseEvent<HTMLButtonElement>) => void
  handleDeleteAll: (e: React.MouseEvent<HTMLButtonElement>) => void
}

function useMultiRoomGroupData(
  items: Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem>,
  referenceUserCartItemData: UserCartHotelItemData | UserCartBedbankHotelItemData,
): MultiRoomGroupData {
  const dispatch = useAppDispatch()
  const itemStates = useAppSelector(state => state.auth.userCart.itemStates)
  const cartOffers = useAppSelector(selectCartOffers)
  const { items: cartItemsData, fetching: cartItemsFetching, error } = useAllUserCartItemsMetadata(items)

  const areAllItemsSavedForLater = items.length > 0 && items.every(item => itemStates[item.itemId]?.saveForLater)
  const areAllItemsDeleted = items.length > 0 && items.every(item => itemStates[item.itemId]?.deleted)
  const isAnyItemDeleted = items.length > 0 && items.some(item => itemStates[item.itemId]?.deleted)

  const fetching = cartItemsFetching && !areAllItemsDeleted

  const cartItemsDataById = useMemo(() => {
    return cartItemsData.reduce((acc, item) => {
      acc[item.itemId] = item
      return acc
    }, {} as Record<string, CartItemData>)
  }, [cartItemsData])

  const isAnyItemOnPastDates = items.some(item => dateIsBeforeOrSame(new Date(item.checkIn), new Date()))
  const isAnyItemUnavailable = items.some(item => {
    const offer = cartOffers[item.offerId]
    const itemData = cartItemsDataById[item.itemId]

    if (!offer) return false

    if (isLEHotel(offer)) {
      const endDate = offer.availabilitySchedule?.end
      if (!endDate) return false
      return !itemData.isDeleted && new Date() > new Date(endDate)
    }

    return false // no 'unavailable' state for bedbank offers
  })

  const isAnyItemSoldOut = items.some(item => {
    const offer = cartOffers[item.offerId]
    const itemData = cartItemsDataById[item.itemId]
    if (!offer) return false
    if (isLEHotel(offer)) {
      return !itemData.isDeleted && offer.isSoldOut
    }

    return !itemData.isDeleted && !itemData?.itemTotals
  })

  const { hotelName, publicTotalPrice } = useMemo(() => {
    let publicTotalPrice

    if (cartItemsData.length > 0) {
      publicTotalPrice = cartItemsData.reduce((acc, cartItem) => {
        const itemData = cartItemsDataById[cartItem.itemId]
        if (itemData) {
          const { publicTotal } = getUserCartItemDisplayTotals(itemData.itemTotals)
          return acc + publicTotal
        }
        return acc
      }, 0)
    }

    return {
      hotelName: referenceUserCartItemData.offer?.property.name || referenceUserCartItemData.offer?.name || '',
      publicTotalPrice,
    }
  }, [referenceUserCartItemData, cartItemsData, cartItemsDataById])

  const handleSaveAllForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!areAllItemsSavedForLater) {
      dispatch(userCartItemsSaveForLater(cartItemsData.filter(item => !item.isSavedForLater).map(item => item.itemId)))
    }
    else {
      dispatch(userCartItemsSaveForLater(cartItemsData.map(item => item.itemId)))
    }
  }, [dispatch, cartItemsData, areAllItemsSavedForLater])

  const handleDeleteAll = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!areAllItemsDeleted) {
      dispatch(userCartItemsDelete(cartItemsData.filter(item => !item.isDeleted).map(item => item.itemId)))
    }
  }, [dispatch, cartItemsData, areAllItemsDeleted])

  const bannerTypeVariant = getBannerTypeVariant(isAnyItemOnPastDates, isAnyItemSoldOut, isAnyItemUnavailable, !!error)

  return {
    hotelName,
    publicTotalPrice,
    fetching,
    error,
    areAllItemsSavedForLater,
    areAllItemsDeleted,
    isAnyItemDeleted,
    groupDisabled: isAnyItemUnavailable || isAnyItemSoldOut || isAnyItemOnPastDates,
    bannerTypeVariant,
    cartItemsData,
    handleSaveAllForLater,
    handleDeleteAll,
  }
}

export default useMultiRoomGroupData
